import React, { useEffect, useRef, useState } from "react";
import Palpation from "./Palpation";
import * as api from "../../../common/manipurCommon/API";
import Utils from "../../../common/manipurCommon/Utils";
import Swal from "sweetalert2";
import RangeOfMotion from "./RangeOfMotion";
import StrengthTesting from "./StrengthTesting";
import IsokineticTesting from "./IsokineticTesting";
import SpecialTests from "./SpecialTests";

const steps = ["", "", "", ""];

interface InjAsmntTabsProps {
    resumeDta?: any;
}

const AsmntTabs: React.FC<InjAsmntTabsProps> = ({ resumeDta }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        athleteId: "",
        assessment_date: "",
        palpation_right: " ",
        palpation_left: " ",
    });
    const [stepData, setStepData] = useState<any>(null);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (resumeDta) {
            setStepData(resumeDta);
            setCurrentStep((resumeDta.current_step) - 1);
        } else {
            setStepData(null);
            setCurrentStep(0);
        }
    }, [resumeDta]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string, option: any) => {
        setFormData((prev) => ({
            ...prev,
            [name]: option ? option.value : "",
        }));
    };

    const back = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

    // ✅ STEP 1 SUBMIT
    const next = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (!formRef.current?.checkValidity()) {
            formRef.current?.reportValidity();
            return;
        }

        const frmDta = new FormData();
        frmDta.append("athlete_id", formData.athleteId);
        frmDta.append("assessment_date", formData.assessment_date);
        frmDta.append("palpation_right", formData.palpation_right);
        frmDta.append("palpation_left", formData.palpation_left);

        fetch(api.SaveAssessment, {
            method: "POST",
            headers: {
                Authorization: "Bearer " + Utils.getAccessToken(),
            },
            body: frmDta,
        })
            .then((res) => res.json())
            .then((result) => {
                if (!result.error) {
                    setStepData(result.data);
                    setCurrentStep((prev) => prev + 1);
                } else {
                    if (result.code === 401) Utils.logoutUser();

                    Swal.fire({
                        title: "Error",
                        text: result.message,
                        icon: "error",
                    });
                }
            });
    };

      // ✅ COMMON SKIP FOR NEXT STEPS
    function handleSkip(aassessId: string, stepId: string, endpoint: string) {

        fetch(api.ListOverallAssessment + "/" + aassessId + "/skip" + "/" + stepId, {
            method: "GET",
            headers: {
                Authorization: "Bearer " + Utils.getAccessToken(),
            },
        })
            .then((res) => res.json())
            .then((result) => {
                if (!result.error) {                   
                        setStepData(result.data);
                        setCurrentStep((prev) => prev + 1);
                } else {
                    if (result.code === 401) Utils.logoutUser();
                }
            })
            .catch((err) => console.error(err));
    }

    // ✅ COMMON SUBMIT FOR NEXT STEPS
    function handleSubmit(endpoint: string, data: any[]) {

        const formData = new FormData();
        formData.append("results", JSON.stringify(data));

        fetch(api.parentAPI2 + endpoint, {
            method: "POST",
            headers: {
                Authorization: "Bearer " + Utils.getAccessToken(),
            },
            body: formData,
        })
            .then((res) => res.json())
            .then((result) => {
                if (!result.error) {
                    if (endpoint.includes("/complete")) {
                        Swal.fire({
                            title: "Success",
                            text: result.message,
                            icon: "success",
                            confirmButtonText: 'OK',
                            allowOutsideClick: false,
                            allowEscapeKey: false
                        }).then((res) => {
                            if (res.isConfirmed) {
                                window.location.reload(); // 🔁 Reload only after OK is clicked
                            }
                        });
                    } else {
                        setStepData(result.data);
                        setCurrentStep((prev) => prev + 1);
                    }
                } else {
                    if (result.code === 401) Utils.logoutUser();
                }
            })
            .catch((err) => console.error(err));
    }

    return (
        <div className="card p-3">
            {/* ✅ Stepper */}
            <div className="stepper-container d-flex mb-3">
                {steps.map((_, index) => (
                    <div
                        key={index}
                        className={`step-circle ${index <= currentStep ? "active" : ""}`}
                    />
                ))}
            </div>

            {/* ✅ Step Content */}
            <form ref={formRef} onSubmit={(e) => e.preventDefault()}>
                {currentStep === 0 && (
                    <Palpation formData={formData} onChange={handleChange} onSelectChange={handleSelectChange}/>
                )}

                {/* ✅ Dynamic rendering */}
                {currentStep > 0 && stepData?.ui_layout === "TABLE" && (
                    <RangeOfMotion
                        stepData={stepData}
                        onSkip={handleSkip}
                        onSubmit={handleSubmit}
                    />
                )}

                {currentStep > 0 &&
                    stepData?.ui_schema?.layout ===
                    "DEPENDENT_DROPDOWN_ROW" && (
                        <StrengthTesting
                            stepData={stepData}
                        onSkip={handleSkip}
                            onSubmit={handleSubmit}
                        />
                    )}
                {currentStep > 0 &&
                    stepData?.ui_schema?.layout ===
                    "NESTED_PARAMETER_FORM" && (
                        <IsokineticTesting
                            stepData={stepData}
                        onSkip={handleSkip}
                            onSubmit={handleSubmit}
                        />
                    )}
                {currentStep > 0 &&
                    stepData?.ui_schema?.layout ===
                    "REPEATER_DYNAMIC_FORM" && (
                        <SpecialTests
                            stepData={stepData}
                            onSubmit={handleSubmit}
                        />
                    )}

                {/* ✅ Navigation */}
                <div className="d-flex justify-content-between mt-3">
                    {currentStep === 0 && (
                        <>
                            {/* <button
                                type="button"
                                disabled={currentStep === 0}
                                onClick={back}
                            >
                                Back
                            </button> */}

                            <div>
                                <button type="button" className="btn btn-primary me-2">
                                    Skip
                                </button>

                                <button type="button" className="btn btn-secondary" onClick={next}>
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </form>
        </div>
    );
};

export default AsmntTabs;